import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Delete } from "lucide-react";
import { Sigil } from "@/components/temple/Sigil";

const key = (uid) => `tt-app-lock-${uid}`;
const RELOCK_MS = 30000;

const hash = async (uid, pin) => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`temple:${uid}:${pin}`));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
};

export const lockEnabled = (uid) => {
  try {
    return Boolean(localStorage.getItem(key(uid)));
  } catch {
    return false;
  }
};
export const setLock = async (uid, pin) => localStorage.setItem(key(uid), await hash(uid, pin));
export const clearLock = (uid) => localStorage.removeItem(key(uid));
const checkPin = async (uid, pin) => localStorage.getItem(key(uid)) === (await hash(uid, pin));

export const Keypad = ({ title, sub, onComplete, error, footer }) => {
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(0);
  const press = useCallback(
    (d) => {
      if (pin.length >= 4) return;
      const next = pin + d;
      setPin(next);
      if (next.length === 4) {
        Promise.resolve(onComplete(next)).then((ok) => {
          if (ok === false) setShake((s) => s + 1);
          setTimeout(() => setPin(""), ok === false ? 350 : 0);
        });
      }
    },
    [pin, onComplete],
  );
  useEffect(() => {
    const onKey = (e) => {
      if (/^\d$/.test(e.key)) press(e.key);
      if (e.key === "Backspace") setPin((p) => p.slice(0, -1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press]);
  return (
    <div className="flex flex-col items-center text-center">
      <Sigil className="w-12 h-12" />
      <p data-testid="keypad-title" className="mt-6 font-display text-3xl">{title}</p>
      {sub && <p className="mt-2 text-sm text-[#A8998C] max-w-xs">{sub}</p>}
      <motion.div key={shake} animate={shake ? { x: [0, -12, 12, -8, 8, 0] } : {}} transition={{ duration: 0.35 }} className="mt-8 flex gap-4" data-testid="keypad-dots">
        {[0, 1, 2, 3].map((k) => (
          <span key={k} className={`w-3.5 h-3.5 rounded-full border border-[#D9B47A] transition-colors duration-200 ${pin.length > k ? "bg-[#D9B47A]" : ""}`} />
        ))}
      </motion.div>
      <p data-testid="keypad-error" className="mt-4 h-5 text-sm text-[#E8838E]">{error}</p>
      <div className="mt-4 grid grid-cols-3 gap-4">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"].map((d) =>
          d === "" ? <span key="blank" /> : (
            <button
              key={d}
              data-testid={`keypad-${d}`}
              aria-label={d === "del" ? "Delete" : d}
              onClick={() => (d === "del" ? setPin((p) => p.slice(0, -1)) : press(d))}
              className="w-[72px] h-[72px] rounded-full border border-[#D9B47A]/25 font-display text-3xl text-[#F2E8DF] active:bg-[#D9B47A]/20 hover:border-[#D9B47A]/60 transition-colors flex items-center justify-center"
            >
              {d === "del" ? <Delete className="w-5 h-5 text-[#A8998C]" /> : d}
            </button>
          ),
        )}
      </div>
      {footer}
    </div>
  );
};

export const LockScreen = ({ uid, title, onUnlock, onForgot }) => {
  const [error, setError] = useState("");
  const [tries, setTries] = useState(0);
  const done = async (pin) => {
    if (await checkPin(uid, pin)) {
      onUnlock();
      return true;
    }
    setTries((t) => t + 1);
    setError(tries + 1 >= 5 ? "Forgotten it? Sign out below to reset your passcode." : "That passcode isn’t right.");
    return false;
  };
  return (
    <div data-testid="app-lock-screen" className="fixed inset-0 z-[200] bg-[#0A0706] flex items-center justify-center px-6">
      <Keypad
        title={title}
        sub="Enter your passcode"
        error={error}
        onComplete={done}
        footer={
          <button data-testid="app-lock-forgot" onClick={onForgot} className="mt-8 text-sm text-[#A8998C] underline underline-offset-4 hover:text-[#F2E8DF]">
            Forgot passcode? Sign out
          </button>
        }
      />
    </div>
  );
};

// Locks when the app opens and again after it has been in the background for 30 seconds.
export const useAppLock = (uid) => {
  const [locked, setLocked] = useState(() => (uid ? lockEnabled(uid) : false));
  useEffect(() => {
    if (uid) setLocked(lockEnabled(uid));
  }, [uid]);
  useEffect(() => {
    if (!uid) return undefined;
    let hiddenAt = 0;
    const onVis = () => {
      if (document.hidden) hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt > RELOCK_MS && lockEnabled(uid)) setLocked(true);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [uid]);
  return [locked, setLocked];
};
